import React, { useCallback, useEffect, useState } from 'react';
import {
  CircularProgress,
  Box,
  Typography,
  Button,
  Chip,
  LinearProgress,
  Grid,
  useTheme,
  Avatar,
  alpha,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import CodeIcon from '@mui/icons-material/Code';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import proxy from '../../api/axios';
import { useDispatch, useSelector } from 'react-redux';
import { showAuth } from '../../redux/authSlice';
import { Helmet } from 'react-helmet';
import ProblemPage from '../ProblemPage';
import { useNavigate } from 'react-router-dom';
import { trophyColors } from '../LeaderboardPage';
import PreviousChallenges from './PreviousChallenges';
import { resolveProfileImage } from '../../common/utils';

const ACCENT = '#137274';

const rules = [
  'Solve one coding problem every day without skipping.',
  'Miss a day and your streak resets.',
  'Daily challenge resets at midnight IST.',
  'Track progress for 75 consecutive days.',
  'Compete on the leaderboard for accountability.',
  'Stay consistent — consistency beats intensity.',
];

const calculateProgress = (completed, total) => {
  if (!total) return 0;
  return Math.min(100, (completed / total) * 100);
};

const stripHtml = (html = '') =>
  String(html)
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const StatPill = ({ icon, label, value, isDark }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 110,
      px: 1.75,
      py: 1.25,
      borderRadius: 2,
      border: `1px solid ${isDark ? '#30363d' : 'rgba(15,60,70,0.1)'}`,
      bgcolor: isDark ? alpha('#fff', 0.03) : alpha(ACCENT, 0.04),
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
      {icon}
      <Typography
        variant="caption"
        sx={{ color: isDark ? '#8b949e' : '#5f6b73', fontWeight: 600, letterSpacing: '0.04em' }}
      >
        {label}
      </Typography>
    </Box>
    <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', letterSpacing: '-0.02em' }}>
      {value}
    </Typography>
  </Box>
);

const ChallengeDetailsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  const loggedInUser = useSelector((state) => state.user.userInfo);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [users, setUsers] = useState([]);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(true);
  const [loadingProblem, setLoadingProblem] = useState(true);
  const [todaysProblem, setTodaysProblem] = useState(null);
  const [enrolled, setEnrolled] = useState(false);
  const [assigned, setAssigned] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [startSolving, setStartSolving] = useState(false);
  const [showLeaderBoard, setShowLeaderBoard] = useState(true);
  const [joining, setJoining] = useState(false);

  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.1)';
  const surface = isDark ? '#161b22' : '#ffffff';
  const pageBg = isDark
    ? '#0f1419'
    : 'linear-gradient(180deg, #eef6f8 0%, #f7f9fb 40%, #ffffff 100%)';
  const muted = isDark ? '#8b949e' : '#5f6b73';

  const fetchLeaderboard = useCallback(async () => {
    try {
      setLoadingLeaderboard(true);
      const response = await proxy.get('/challenge/leaderboard');
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error('Error fetching leaderboard');
    } finally {
      setLoadingLeaderboard(false);
    }
  }, []);

  const fetchTodaysProblem = useCallback(async () => {
    try {
      setLoadingProblem(true);
      const response = await proxy.get('/challenge/todays-problem');
      const data = response.data || {};

      setEnrolled(Boolean(data.enrolled));
      setAssigned(Boolean(data.assigned));
      setStatusMessage(data.message || '');
      setTodaysProblem(data.problem || null);
    } catch (error) {
      const message = error.response?.data?.message || '';
      if (message.includes('not enrolled')) {
        setEnrolled(false);
        setAssigned(false);
        setTodaysProblem(null);
        setStatusMessage(message);
      } else if (message.includes('No problem assigned')) {
        setEnrolled(true);
        setAssigned(false);
        setTodaysProblem(null);
        setStatusMessage(message);
      } else {
        setTodaysProblem(null);
        setStatusMessage(message || "Could not load today's challenge.");
      }
    } finally {
      setLoadingProblem(false);
    }
  }, []);

  const fixStreak = async (userId) => {
    try {
      const response = await proxy.post('/challenge/fix-streak', { userId });
      toast.success(response.data?.message || 'Streak fixed');
      await fetchLeaderboard();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error fixing streak');
    }
  };

  const join75HardChallenge = async () => {
    if (!isLoggedIn) {
      dispatch(showAuth());
      return;
    }
    try {
      setJoining(true);
      const response = await proxy.post('/challenge/join-75-hard');
      toast.success(response.data?.message || 'Joined successfully');
      await Promise.all([fetchTodaysProblem(), fetchLeaderboard()]);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error joining the 75 Hard Coding challenge');
    } finally {
      setJoining(false);
    }
  };

  const handleUserClick = (username) => {
    if (username) navigate(`/user/${username}`);
  };

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  useEffect(() => {
    fetchTodaysProblem();
  }, [fetchTodaysProblem, isLoggedIn]);

  useEffect(() => {
    if (!startSolving) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setStartSolving(false);
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [startSolving]);

  const submissionCallBack = async (data) => {
    if (data?.status === 'Accepted') {
      toast.success("Congratulations, you have solved today's problem");
      setTodaysProblem((prev) => (prev ? { ...prev, isSolved: true } : prev));
      await fetchLeaderboard();
    }
  };

  const previewText = stripHtml(todaysProblem?.description || '').slice(0, 140);
  const dayNumber = todaysProblem?.day || 0;
  const streak = todaysProblem?.streak ?? 0;
  const progressPct = calculateProgress(dayNumber, 75);
  const topThree = users.slice(0, 3);

  const renderTodaySection = () => {
    if (loadingProblem) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 5, gap: 1.5 }}>
          <CircularProgress size={22} sx={{ color: ACCENT }} />
          <Typography variant="body2" sx={{ color: muted }}>
            Loading today’s challenge…
          </Typography>
        </Box>
      );
    }

    if (todaysProblem) {
      return (
        <Box
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${border}`,
            bgcolor: surface,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: { xs: 2, sm: 2.5 },
              py: 1.5,
              borderBottom: `1px solid ${border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
              Today&apos;s challenge
            </Typography>
            <Chip
              size="small"
              label="Resets midnight IST"
              sx={{
                height: 22,
                fontSize: '0.7rem',
                fontWeight: 600,
                bgcolor: isDark ? alpha(ACCENT, 0.2) : alpha(ACCENT, 0.1),
                color: ACCENT,
              }}
            />
          </Box>

          <Box
            sx={{
              p: { xs: 2, sm: 2.5 },
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 2,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                <CodeIcon sx={{ color: ACCENT, fontSize: 22 }} />
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    color: isDark ? '#e6edf3' : '#1a2b2c',
                  }}
                >
                  {todaysProblem.title}
                </Typography>
                {todaysProblem.isSolved && (
                  <Chip
                    size="small"
                    icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
                    label="Solved"
                    color="success"
                    sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700 }}
                  />
                )}
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: muted,
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  lineHeight: 1.5,
                }}
              >
                {previewText}
                {(todaysProblem.description || '').length > 140 ? '…' : ''}
              </Typography>
            </Box>

            <Button
              variant="contained"
              size="medium"
              startIcon={todaysProblem.isSolved && !startSolving ? <CheckCircleIcon /> : undefined}
              onClick={() => setStartSolving((v) => !v)}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                px: 2.5,
                whiteSpace: 'nowrap',
                alignSelf: { xs: 'stretch', sm: 'center' },
                bgcolor: todaysProblem.isSolved ? '#1a7f37' : ACCENT,
                '&:hover': {
                  bgcolor: todaysProblem.isSolved ? '#15632c' : '#0f5c5e',
                },
              }}
            >
              {startSolving ? 'Hide editor' : todaysProblem.isSolved ? 'View / Solved' : 'Solve now'}
            </Button>
          </Box>

          {startSolving && (
            <Box
              sx={{
                position: 'fixed',
                inset: 0,
                zIndex: 1400,
                bgcolor: isDark ? '#0d1117' : '#f4f8f9',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  flexShrink: 0,
                  px: { xs: 1.5, sm: 2.5 },
                  py: 1,
                  borderBottom: `1px solid ${border}`,
                  bgcolor: surface,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <CodeIcon sx={{ color: ACCENT, fontSize: 20, flexShrink: 0 }} />
                  <Typography
                    noWrap
                    sx={{ fontWeight: 700, fontSize: '0.9rem' }}
                  >
                    75 Hard · {todaysProblem.title}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  onClick={() => setStartSolving(false)}
                  startIcon={<CloseIcon fontSize="small" />}
                  sx={{ textTransform: 'none', fontWeight: 600, flexShrink: 0, color: muted }}
                >
                  Back to challenge
                </Button>
              </Box>
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                <ProblemPage
                  callBack={submissionCallBack}
                  challenge="75Hard"
                  problem={todaysProblem}
                />
              </Box>
            </Box>
          )}
        </Box>
      );
    }

    if (enrolled && !assigned) {
      return (
        <Box
          sx={{
            textAlign: 'center',
            py: 4,
            px: 2,
            borderRadius: 2.5,
            border: `1px dashed ${border}`,
            bgcolor: surface,
          }}
        >
          <Typography sx={{ fontWeight: 700, mb: 0.75 }}>
            You&apos;re enrolled — waiting on today&apos;s problem
          </Typography>
          <Typography variant="body2" sx={{ color: muted, mb: 2, maxWidth: 420, mx: 'auto' }}>
            {statusMessage || 'Daily problems are assigned around midnight IST.'}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchTodaysProblem}
            sx={{ textTransform: 'none', fontWeight: 600, borderColor: ACCENT, color: ACCENT }}
          >
            Refresh
          </Button>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          textAlign: 'center',
          py: { xs: 4, sm: 5 },
          px: 2,
          borderRadius: 2.5,
          border: `1px solid ${border}`,
          bgcolor: surface,
          background: isDark
            ? `linear-gradient(160deg, ${alpha(ACCENT, 0.12)} 0%, ${surface} 55%)`
            : `linear-gradient(160deg, ${alpha(ACCENT, 0.08)} 0%, #fff 55%)`,
        }}
      >
        <FitnessCenterIcon sx={{ fontSize: 40, color: ACCENT, mb: 1.5 }} />
        <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', mb: 0.75, letterSpacing: '-0.02em' }}>
          Ready for 75 days of consistency?
        </Typography>
        <Typography variant="body2" sx={{ color: muted, mb: 2.5, maxWidth: 400, mx: 'auto' }}>
          Join the challenge, get one problem a day, and build an unbreakable streak.
        </Typography>
        <Button
          variant="contained"
          size="large"
          startIcon={joining ? <CircularProgress size={18} color="inherit" /> : <FitnessCenterIcon />}
          disabled={joining}
          onClick={join75HardChallenge}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            px: 3,
            bgcolor: ACCENT,
            '&:hover': { bgcolor: '#0f5c5e' },
          }}
        >
          {joining ? 'Joining…' : 'Start challenge'}
        </Button>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: pageBg,
        px: { xs: 1.5, sm: 2.5 },
        py: { xs: 2.5, md: 3.5 },
      }}
    >
      <Helmet>
        <title>75 Hard Coding Challenge | CrafterHack</title>
        <meta
          name="description"
          content="Solve one DSA problem every day for 75 days on CrafterHack."
        />
      </Helmet>

      <Box sx={{ maxWidth: 980, mx: 'auto' }}>
        {/* Hero */}
        <Box
          sx={{
            mb: 2.5,
            p: { xs: 2, sm: 2.75 },
            borderRadius: 2.5,
            border: `1px solid ${border}`,
            bgcolor: surface,
            background: isDark
              ? `linear-gradient(135deg, ${alpha(ACCENT, 0.18)} 0%, #161b22 50%, ${alpha('#ff9800', 0.08)} 100%)`
              : `linear-gradient(135deg, ${alpha(ACCENT, 0.1)} 0%, #ffffff 55%, ${alpha('#ff9800', 0.08)} 100%)`,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.75, minWidth: 0 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha('#ff9800', isDark ? 0.2 : 0.15),
                flexShrink: 0,
              }}
            >
              <EmojiEventsIcon sx={{ color: '#ff9800', fontSize: 28 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                <Typography
                  component="h1"
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                    letterSpacing: '-0.03em',
                    lineHeight: 1.2,
                  }}
                >
                  75 Hard Coding Challenge
                </Typography>
                <Chip
                  size="small"
                  label="Live"
                  sx={{
                    height: 22,
                    fontWeight: 700,
                    fontSize: '0.65rem',
                    bgcolor: '#1a7f37',
                    color: '#fff',
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: muted, maxWidth: 480, lineHeight: 1.5 }}>
                One problem a day for 75 days. Build discipline, ship consistency, climb the streak board.
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Progress stats — only meaningful when enrolled / has day data */}
        {(enrolled || dayNumber > 0) && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mb: 2.5 }}>
            <StatPill
              isDark={isDark}
              icon={<AssignmentTurnedInIcon sx={{ fontSize: 16, color: ACCENT }} />}
              label="DAY"
              value={`${dayNumber} / 75`}
            />
            <StatPill
              isDark={isDark}
              icon={<LocalFireDepartmentIcon sx={{ fontSize: 16, color: '#e65100' }} />}
              label="STREAK"
              value={streak}
            />
            <StatPill
              isDark={isDark}
              icon={<CheckCircleIcon sx={{ fontSize: 16, color: '#1a7f37' }} />}
              label="PROGRESS"
              value={`${Math.round(progressPct)}%`}
            />
          </Box>
        )}

        {(enrolled || dayNumber > 0) && (
          <Box sx={{ mb: 2.5 }}>
            <LinearProgress
              variant="determinate"
              value={progressPct}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: isDark ? alpha('#fff', 0.08) : alpha(ACCENT, 0.12),
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  bgcolor: ACCENT,
                },
              }}
            />
          </Box>
        )}

        {/* Today */}
        <Box sx={{ mb: 3 }}>{renderTodaySection()}</Box>

        {/* Top streakers */}
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '1.05rem',
              letterSpacing: '-0.02em',
              mb: 1.5,
            }}
          >
            Top streakers
          </Typography>

          {loadingLeaderboard ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} sx={{ color: ACCENT }} />
            </Box>
          ) : topThree.length === 0 ? (
            <Typography variant="body2" sx={{ color: muted }}>
              No participants yet — be the first.
            </Typography>
          ) : (
            <Grid container spacing={1.5}>
              {topThree.map((user, index) => (
                <Grid item xs={12} sm={4} key={user.id || user.username || index}>
                  <Box
                    onClick={() => handleUserClick(user.username)}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: `1px solid ${border}`,
                      bgcolor: surface,
                      cursor: 'pointer',
                      height: '100%',
                      transition: 'border-color 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        borderColor: alpha(ACCENT, 0.45),
                        boxShadow: isDark
                          ? 'none'
                          : `0 8px 20px ${alpha(ACCENT, 0.08)}`,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.25 }}>
                      <EmojiEventsIcon sx={{ color: trophyColors[index] || '#ff9800', fontSize: 22 }} />
                      <Avatar
                        src={resolveProfileImage(user.profileImage)}
                        sx={{
                          width: 36,
                          height: 36,
                          fontSize: '0.9rem',
                          fontWeight: 700,
                          bgcolor: alpha(ACCENT, 0.15),
                          color: ACCENT,
                        }}
                      >
                        {(user.name?.[0] || user.username?.[0] || '?').toUpperCase()}
                      </Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.9rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {user.name || user.username}
                        </Typography>
                        <Typography variant="caption" sx={{ color: muted }}>
                          @{user.username}
                        </Typography>
                      </Box>
                      <PersonIcon sx={{ fontSize: 18, color: muted }} />
                    </Box>
                    <Chip
                      size="small"
                      icon={<LocalFireDepartmentIcon sx={{ fontSize: '14px !important' }} />}
                      label={`${user.streak} day streak · max ${user.maxStreak}`}
                      sx={{
                        height: 24,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        bgcolor: isDark ? alpha('#e65100', 0.2) : alpha('#e65100', 0.08),
                        color: isDark ? '#ffab40' : '#bf360c',
                        '& .MuiChip-icon': { color: 'inherit' },
                      }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        <PreviousChallenges />

        {/* Leaderboard */}
        <Box
          sx={{
            mt: 3,
            mb: 3,
            p: { xs: 2, sm: 2.5 },
            borderRadius: 2.5,
            border: `1px solid ${border}`,
            bgcolor: surface,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              mb: showLeaderBoard ? 2 : 0,
              flexWrap: 'wrap',
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', letterSpacing: '-0.02em' }}>
                Leaderboard
              </Typography>
              <Typography variant="body2" sx={{ color: muted, fontSize: '0.8rem' }}>
                {loadingLeaderboard
                  ? 'Loading…'
                  : `${users.length} participant${users.length === 1 ? '' : 's'} · ranked by streak`}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<LeaderboardIcon />}
              onClick={() => setShowLeaderBoard((v) => !v)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderColor: border,
                color: isDark ? '#e6edf3' : '#1a2b2c',
              }}
            >
              {showLeaderBoard ? 'Hide' : 'Show'}
            </Button>
          </Box>

          {showLeaderBoard &&
            (loadingLeaderboard ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} sx={{ color: ACCENT }} />
              </Box>
            ) : users.length === 0 ? (
              <Typography variant="body2" sx={{ color: muted, py: 2 }}>
                No participants on the board yet.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {users.map((user, index) => (
                  <Box
                    key={user.id || index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 1.5,
                      py: 1.15,
                      borderRadius: 1.5,
                      border: `1px solid ${border}`,
                      bgcolor: isDark ? alpha('#fff', 0.02) : alpha(ACCENT, 0.02),
                      transition: 'background 0.15s',
                      '&:hover': {
                        bgcolor: isDark ? alpha('#fff', 0.05) : alpha(ACCENT, 0.06),
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 800,
                        fontSize: '0.75rem',
                        flexShrink: 0,
                        bgcolor:
                          index < 3
                            ? alpha(trophyColors[index] || '#ff9800', 0.2)
                            : isDark
                              ? alpha('#fff', 0.08)
                              : alpha('#000', 0.06),
                        color: index < 3 ? trophyColors[index] : muted,
                      }}
                    >
                      {index + 1}
                    </Box>

                    <Box
                      onClick={() => handleUserClick(user.username)}
                      sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: index < 3 ? 700 : 600,
                            fontSize: '0.9rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {user.name || user.username}
                        </Typography>
                        {user.status === 'completed' && (
                          <Chip
                            size="small"
                            label="Done"
                            sx={{
                              height: 18,
                              fontSize: '0.62rem',
                              fontWeight: 800,
                              flexShrink: 0,
                              bgcolor: isDark ? alpha('#1a7f37', 0.25) : alpha('#1a7f37', 0.12),
                              color: '#1a7f37',
                            }}
                          />
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ color: muted }}>
                        🔥 {user.streak} days · max {user.maxStreak}
                        {user.dayCount != null ? ` · day ${user.dayCount}/75` : ''}
                      </Typography>
                    </Box>

                    {loggedInUser?.role === 'admin' && user.streak !== user.maxStreak && (
                      <Button
                        size="small"
                        onClick={() => fixStreak(user.id)}
                        sx={{ textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
                      >
                        Fix streak
                      </Button>
                    )}
                  </Box>
                ))}
              </Box>
            ))}
        </Box>

        {/* Rules */}
        <Box
          sx={{
            p: { xs: 2, sm: 2.5 },
            borderRadius: 2.5,
            border: `1px solid ${border}`,
            bgcolor: surface,
            mb: 2,
          }}
        >
          <Typography
            sx={{ fontWeight: 800, fontSize: '1.05rem', letterSpacing: '-0.02em', mb: 1.5 }}
          >
            Rules & guidelines
          </Typography>
          <Grid container spacing={1.25}>
            {rules.map((rule) => (
              <Grid item xs={12} sm={6} key={rule}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <AssignmentTurnedInIcon
                    sx={{ fontSize: 18, color: ACCENT, mt: 0.2, flexShrink: 0 }}
                  />
                  <Typography variant="body2" sx={{ color: muted, fontSize: '0.85rem', lineHeight: 1.45 }}>
                    {rule}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default ChallengeDetailsPage;
